import { DarkChapter } from '@/components/layout/DarkChapter';
import { AudioWaveform } from '@/components/AudioWaveform';
import { SimpleReveal } from '@/components/SimpleReveal';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { trackLead } from '@/lib/metaPixel';
import { Footer } from '@/components/Footer';

const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";

export default function ReleaseGuide() {
  return (
    <DarkChapter>
      {/* Hero Section */}
      <section className="relative flex flex-col items-center px-6 pt-16 lg:pt-24 pb-8 lg:pb-12">
        <div className="w-full max-w-5xl mx-auto flex flex-col items-center text-center">

          {/* Badge */}
          <SimpleReveal>
            <div className="mb-4 flex flex-col items-center">
              <AudioWaveform />
              <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md px-2 lg:px-2.5 py-1 lg:py-1.5 gap-2">
                <span className="font-mono text-[10px] lg:text-xs text-white/50 tracking-[0.15em] uppercase">
                  &gt; Release Guide
                </span>
              </div>
            </div>
          </SimpleReveal>

          {/* Title */}
          <SimpleReveal delay={80}>
            <h1 className="font-headline font-bold text-white text-center mb-4 text-[clamp(2rem,5vw,3.5rem)] leading-[1.1] tracking-[-0.03em] max-w-4xl mx-auto">
              How To Release A Single In 2026
            </h1>
          </SimpleReveal>

          {/* Subheading */}
          <SimpleReveal delay={140}>
            <p className="text-sm lg:text-base text-white/70 text-center max-w-xl mx-auto mb-8 leading-[1.6]" style={{ letterSpacing: '0.3px' }}>
              Your official plan video guide
            </p>
          </SimpleReveal>

          {/* Video */}
          <SimpleReveal delay={200} className="w-full">
            <div className="w-full max-w-3xl mx-auto relative">
              {/* Static noise particles */}
              <div className="absolute -inset-24 pointer-events-none opacity-[0.08]" style={{
                backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.15) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
                maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)'
              }} />

              {/* Green ambient glow */}
              <div className="absolute -inset-20 rounded-3xl blur-3xl pointer-events-none" style={{
                background: 'radial-gradient(ellipse at center, rgba(0, 113, 227, 0.35) 0%, rgba(0, 113, 227, 0.15) 50%, transparent 85%)'
              }} />

              {/* Video container */}
              <div className="relative rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.6)] border border-white/10 transition-all duration-500 hover:shadow-[0_12px_50px_rgba(0,0,0,0.7)] hover:-translate-y-1">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe
                    src="https://player.vimeo.com/video/1169832194?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="How To Release A Single In 2026 (The 20 Day Plan)"
                  />
                </div>
              </div>
            </div>
          </SimpleReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative pt-6 lg:pt-8 pb-12 lg:pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <SimpleReveal>
            <h2 className="font-headline font-bold text-white text-2xl md:text-3xl mb-4 tracking-tight">
              Ready to put this plan into action?
            </h2>
          </SimpleReveal>

          <SimpleReveal delay={80}>
            <p className="text-white/60 text-base md:text-lg mb-8 max-w-md mx-auto leading-relaxed">
              Work with our team on your next release. We'll build the strategy, you bring the music.
            </p>
          </SimpleReveal>

          <SimpleReveal delay={160}>
            <div className="inline-block group relative rounded-xl rotating-border-button">
              <Button variant="hero-blue-ring" size="hero" className="border border-[#0071e3]/40 shadow-[0_0_14px_rgba(0,113,227,0.18)] hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.35)]" asChild>
                <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
                  <span className="inline-flex items-center gap-2">
                    Apply Now
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </a>
              </Button>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={220}>
            <p className="text-white/40 text-sm mt-6">
              Trusted by over 30,000 artists generating 100M+ streams
            </p>
          </SimpleReveal>
        </div>
      </section>

      <Footer />
    </DarkChapter>
  );
}
